import "./pagination.scss";
import { Dispatch, HTMLAttributes, SetStateAction, useMemo } from "react";
import { Pageable } from "../../../../models/interfaces/pageable.interface";
import useIsMobile from "../../../../hooks/use-is-mobile";

interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  pageable?: Pageable<unknown>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  callback?: () => void;
}

export function Pagination({ currentPage, pageable, setCurrentPage, callback, ...props }: PaginationProps) {
  const { isMobile } = useIsMobile();
  
  // Uses 0 index
  function createHandleGoToPage(targetPage: number) {
    return () => {
      callback?.();
      setCurrentPage(targetPage);
    };
  }

  const maxPageSelectionItemsShown = isMobile ? 5 : 7;
  const ellipsis = "...";
  const availablePages = useMemo(() => {
    if(!pageable) {
      return [];
    }

    const numberOfPages = pageable.total_pages;
    if(numberOfPages <= maxPageSelectionItemsShown) {
      return Array.from({ length: numberOfPages }).map((item, index) => (index + 1).toString());
    } else {
      const displayPageItems = Array.from({length: maxPageSelectionItemsShown}).map(item => "1");
      displayPageItems[0] = "1";
      displayPageItems[displayPageItems.length - 1] = numberOfPages.toString();
      let showLeftEllipsis = false;
      let showRightEllipsis = false;
      if(currentPage > 4) {
        showLeftEllipsis = true;
      }

      if(currentPage < numberOfPages - 4) {
        showRightEllipsis = true;
      }

      if(showLeftEllipsis && showRightEllipsis) {
          displayPageItems[1] = ellipsis
          displayPageItems[displayPageItems.length - 2] = ellipsis
          let runningNumber = currentPage - 1;
          for(let i = 2; i<=4; i++){
             displayPageItems[i] = runningNumber.toString()
             runningNumber++;
          }
       } else if(showLeftEllipsis) {
          displayPageItems[1] = ellipsis
          let runningNumber = numberOfPages - 4
          for(let i = 2; i <= 5; i++){
            displayPageItems[i] = runningNumber.toString();
            runningNumber++;
          }
       } else {
         displayPageItems[displayPageItems.length - 2] = ellipsis
         let runningNumber = 2
         for(let i = 1; i <=4; i++){
            displayPageItems[i] = runningNumber.toString();
            runningNumber++;
         }
       }
      return displayPageItems;
    }
  }, [pageable, currentPage, maxPageSelectionItemsShown])

  if (!pageable || pageable.total_pages <= 1) return null;

  return (
    <div className="polymer-pagination__container" {...props}>
      <div className="polymer-pagination__container-inner">
        <button className="polymer-pagination__button" disabled={pageable.first} onClick={createHandleGoToPage(currentPage - 1)}>
          <i className="bi bi-chevron-left polymer-pagination__chevron" />
        </button>
        <div className="polymer-pagination__page-container">
          {availablePages.map((pageNumber, index) => 
          { 
            const key = `${index} - ${pageNumber}`;
            return (
            <button
              key={key}
              className={`polymer-pagination__page-button ${pageNumber !== ellipsis && (Number(pageNumber) - 1) === currentPage ? "polymer-pagination__page-button-active" : ""}`}
              onClick={() => {
                if(pageNumber !== ellipsis) {
                  console.log("move to page", pageNumber)
                  createHandleGoToPage(Number(pageNumber) - 1)();
                }
              }}
            >
              {pageNumber}
            </button>
          )
        })}
        </div>
        <button className="polymer-pagination__button" disabled={pageable.last} onClick={createHandleGoToPage(currentPage + 1)}>
          <i className="bi bi-chevron-right polymer-pagination__chevron" />
        </button>
      </div>
    </div>
  );
}
